import { Subject, merge, zip, range, timer, interval } from "rxjs";
import { retryWhen, mergeMap, mapTo, delay, map } from "rxjs/operators";
import { webSocket } from "rxjs/webSocket";
import randomCorrelationId from "uuid/v4";
import { isUniversal } from "@kofile/ko-search-action-types";
import * as actions from "../../actions";
import * as getters from "../../getters";
import { noop } from "../../../utils";

/**
 * Redux Middelware for connecting a WS socket and our Store
 *
 * @example
 *
 *
 *    socketMiddleware('ws://')(store)(next)(action)
 *
 *
 * @return {Function} - store -> next -> action -> void
 */

const includeList = [
  "fetch-a-refund",
  "fetch-billing-report",
  "fetch-order-for-a-user",
  "fetch-refund-activity-for-a-tenant",
  "fetch-import-report",
  "initiate-import-report",
  "fetch-billing-report-totals",
  "list-platform-reporting-dashboards",
  "get-platform-reporting-embed-url",
];

const isIncluded = (action) =>
  includeList.includes(action.type) || isUniversal(action);

export const socketMiddleware =
  ({ socketUrl, onError = noop, onDone = noop }) =>
  (store) => {
    // Gain a reference to the `dispatch` value
    // so we can tell our Redux store about sockets
    const { dispatch } = store;

    // The actual web socket observable
    const openSubject = new Subject();
    const closeSubject = new Subject();
    const socketStream = webSocket({
      url: socketUrl,
      openObserver: openSubject,
      closeObserver: closeSubject,
    });

    // We do not hold a reference to the subscriptions
    // since we want this subscription to be there
    // for as long as our store is there

    merge(
      socketStream.pipe(
        retryWhen((errors) =>
          zip(range(1, 15), errors).pipe(
            map(([attempts]) => attempts),
            mergeMap((attempts) => timer(attempts * 1000))
          )
        )
      ),
      openSubject.pipe(mapTo(actions.socket.opened())),
      closeSubject.pipe(mapTo(actions.socket.retrying()))
    ).subscribe(dispatch, onError, onDone);

    const pingInterval = 30000;

    interval(pingInterval)
      .pipe(delay(pingInterval))
      .subscribe(() => {
        socketStream.next("PING");
      });

    return (next) => (action) => {
      // We always want to tell the socket about
      // our action
      if (isIncluded(action)) {
        const state = store.getState();
        const jwt = getters.user.jwt(state);
        const countyConfig = getters.county(state) || {};
        const { id: tenantId = null } = countyConfig;

        socketStream.next(
          Object.assign({}, action, {
            sync: true,
            authToken: jwt,
            tenantId,
            correlationId: randomCorrelationId(),
          })
        );
      }

      // and we want to tell our system about this action
      return next(action);
    };
  };

export default socketMiddleware;
